import React, { useState, useEffect } from 'react';
import { Shield, Eye, EyeOff, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Auth Context
const ADMIN_SESSION_KEY = 'parkskill_admin_session';

export const checkAdminAuth = () => {
  const session = sessionStorage.getItem(ADMIN_SESSION_KEY);
  if (!session) return false;
  
  try {
    const { expiry } = JSON.parse(session);
    if (new Date().getTime() > expiry) {
      sessionStorage.removeItem(ADMIN_SESSION_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const setAdminSession = () => {
  const expiry = new Date().getTime() + (2 * 60 * 60 * 1000); // 2 hours
  sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({ expiry }));
};

export const clearAdminSession = () => {
  sessionStorage.removeItem(ADMIN_SESSION_KEY);
};

// Login Component
const AdminLogin = ({ onLoginSuccess }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      
      if (response.data.success) {
        setAdminSession();
        toast.success('Login successful!');
        onLoginSuccess();
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-700" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Access</h1>
            <p className="text-slate-500 mt-2">Enter password to access the dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-700 hover:bg-blue-800"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Access Dashboard'}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-400 mt-6">
            Park Skill Admin Portal
          </p>
        </div>
      </div>
    </div>
  );
};

// Logout Button Component
export const AdminLogoutButton = ({ onLogout }) => {
  const handleLogout = () => {
    clearAdminSession();
    toast.success('Logged out successfully');
    onLogout();
  };

  return (
    <Button 
      onClick={handleLogout}
      variant="outline"
      className="border-red-300 text-red-600 hover:bg-red-50"
    >
      <LogOut className="w-4 h-4 mr-2" />
      Logout
    </Button>
  );
};

export default AdminLogin;
